import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { useCurrentUser } from '@/contexts/auth';
import { useMqtt } from '@/contexts/mqtt-provider-context';
import {
    useDisableStream,
    useGetLocationStreams,
    useStartStream,
    useStopStream,
} from '@/domain/videoStream';

import { applyLocationStreams } from './locationStreamSync';
import {
    type AllButtonState,
    type LocationVideoCage,
    useLocationVideoStore,
} from './locationVideoStore';
import { streamBannerState } from './streamBannerState';
import { mqttTopicsVideo } from './topics';
import { createVideoMessageHandler, isAnotherUserStreaming } from './videoMessages';

export interface LocationVideoStreams {
    activeCages: LocationVideoCage[];
    allButtonState: AllButtonState;
    onlyViewMode: boolean;
    showSpinner: boolean;
    showHello: boolean;
    cageChanged: (bioscopeId: number) => Promise<void>;
    allCagesChanged: (buttonState: boolean) => Promise<void>;
}

/**
 * Streams for every pen at a location: starts and stops them, follows their state over MQTT,
 * and stops whatever this user started when the page closes.
 */
export function useLocationVideoStreams(locationId: number): LocationVideoStreams {
    const user = useCurrentUser();
    const user_id = user?.id ?? null;
    const { client, isConnected, subscribe, unsubscribe } = useMqtt();

    const hasRun0 = useRef(false);
    const hasRun1 = useRef(false);
    const hasRun2 = useRef(false);
    const hasRun3 = useRef(false);

    const { t } = useTranslation();
    const { mutateAsync: startStreamApi } = useStartStream();
    const { mutateAsync: stopStreamApi } = useStopStream();
    const { mutateAsync: disableStreamApi } = useDisableStream();

    // The store, the effects and the MQTT handler fire these and move on, so each failure is
    // handled here. A pen whose start failed resets itself when no stream goes live (errorCheck);
    // a failed stop or disable is logged, as the cage page's player does.
    const startStream = useCallback(
        (request: { bioscopeId: number }) => {
            startStreamApi(request).catch(() => {
                ToastError(t('behaviorModule.videoStream.error'), 'location-video-start-error');
            });
        },
        [startStreamApi, t]
    );
    const stopStream = useCallback(
        (request: { bioscopeId: number }) => {
            stopStreamApi(request).catch(console.error);
        },
        [stopStreamApi]
    );
    const disableStream = useCallback(
        (request: { bioscopeId: number }) => {
            disableStreamApi(request).catch(console.error);
        },
        [disableStreamApi]
    );

    const { data: locationStreams } = useGetLocationStreams(locationId);
    const {
        activeCages,
        initializeCages,
        updateBioscopeOnlineStatus,
        buttonActionHandler,
        updateMuxData,
        updateBioscopeData,
        allButtonState,
        updateAllButtonState,
        onlyViewMode,
        setOnlyViewMode,
        setIsButtonDisabled,
    } = useLocationVideoStore();

    const activeCagesRef = useRef(activeCages);

    useEffect(() => {
        activeCagesRef.current = activeCages;

        if (!hasRun0.current && activeCages && activeCages.length > 0) {
            hasRun0.current = true;
            setOnlyViewMode(isAnotherUserStreaming(activeCages, user_id));
        }

        if (locationStreams && !hasRun1.current && isConnected) {
            hasRun1.current = true;
            initializeCages(locationStreams, startStream, stopStream, disableStream);
            applyLocationStreams(locationStreams, {
                disableStream,
                updateMuxData,
                buttonActionHandler,
            });
        }

        if (!hasRun2.current && isConnected && client && activeCages.length > 0) {
            hasRun2.current = true;
            const handleMQTTMessage = createVideoMessageHandler({
                userId: user_id,
                getActiveCages: () => useLocationVideoStore.getState().activeCages,
                updateBioscopeOnlineStatus,
                updateMuxData,
                buttonActionHandler,
                setOnlyViewMode,
                updateBioscopeData,
            });
            client.on('message', handleMQTTMessage);
        }

        if (!hasRun3.current && isConnected && activeCages.length > 0) {
            hasRun3.current = true;
            for (const cage of activeCages) {
                const bioscopeId: number = cage.bioscopeId;
                const {
                    videoStatus: muxStatusTopic,
                    bioscopeStatus: bioscopeStatusTopic,
                    bioboardData: dataTopic,
                } = mqttTopicsVideo(bioscopeId);

                subscribe(muxStatusTopic, { qos: 1 });
                subscribe(dataTopic, { qos: 1 });

                setTimeout(() => {
                    subscribe(bioscopeStatusTopic, { qos: 1 });
                }, 1000);
            }
        }
    }, [locationStreams, isConnected, activeCages]);

    useEffect(() => {
        return () => {
            for (const cage of activeCagesRef.current) {
                const bioscopeId: number = cage.bioscopeId;
                if (!onlyViewMode) {
                    if (cage.muxData?.status === 'active' || cage.isButtonSelected) {
                        stopStream({ bioscopeId: bioscopeId });
                    }
                }
                unsubscribe(`bioscope/status/${bioscopeId}`);
                unsubscribe(`bioscope/livestream/${bioscopeId}/status`);
                unsubscribe(`bioscope/data/response/${bioscopeId}`);
            }
        };
    }, [onlyViewMode, unsubscribe, stopStream]);

    const errorCheck = async (bioscopeId: number, v: boolean) => {
        if (v) {
            const timer = setTimeout(() => {
                const cage = activeCagesRef.current.find((cage) => cage.bioscopeId === bioscopeId);

                if (cage && cage.muxData?.status !== 'active' && cage.isButtonSelected) {
                    disableStream({ bioscopeId: bioscopeId });
                    buttonActionHandler(bioscopeId, false);
                }
            }, 70000);
        }
    };

    const tempDisableButton = async (bioscopeId: number) => {
        setIsButtonDisabled(bioscopeId, true);
        const timer = setTimeout(() => {
            setIsButtonDisabled(bioscopeId, false);
        }, 5000);
    };

    const cageChanged = async (bioscopeId: number) => {
        const v = activeCages.find((cage) => cage.bioscopeId === bioscopeId);

        buttonActionHandler(bioscopeId);
        updateAllButtonState();

        errorCheck(bioscopeId, !v?.isButtonSelected);
        tempDisableButton(bioscopeId);
        tempDisableButton(0);
    };

    const allCagesChanged = async (buttonState: boolean) => {
        for (const cage of activeCages) {
            if (buttonState === false && cage.isButtonSelected === false) continue;

            if (buttonState === true && cage.isButtonSelected === true) continue;

            if (cage.online && !cage.isOutOfQuota) {
                buttonActionHandler(cage.bioscopeId, buttonState);
                errorCheck(cage.bioscopeId, buttonState);
                tempDisableButton(cage.bioscopeId);
            }
        }
        updateAllButtonState();
        tempDisableButton(0);
    };

    const { showSpinner, showHello } = streamBannerState(activeCages, onlyViewMode);

    return {
        activeCages,
        allButtonState,
        onlyViewMode,
        showSpinner,
        showHello,
        cageChanged,
        allCagesChanged,
    };
}
